import { Injectable, inject } from '@angular/core';
import { collection, doc, getDoc, getDocs, limit, query, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { MemberDetails, OrganizationMember } from '../models/member.model';
import { Role } from '../models/role.model';
import { FirebaseService } from './firebase.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationMemberService {
  private readonly firebase = inject(FirebaseService);

  async getCurrentMember(orgId: string): Promise<OrganizationMember | null> {
    const uid = this.firebase.auth.currentUser?.uid;
    if (!uid) return null;

    const snap = await getDoc(doc(this.firebase.firestore, 'organizations', orgId, 'members', uid));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      userId: data['userId'] ?? uid,
      role: data['role'],
      roleId: data['roleId'],
      permissions: data['permissions'],
      joinedAt: data['joinedAt'],
    };
  }

  async listMembers(orgId: string): Promise<OrganizationMember[]> {
    const membersRef = collection(this.firebase.firestore, 'organizations', orgId, 'members');
    const snapshot = await getDocs(query(membersRef, limit(200)));
    return snapshot.docs.map((d) => {
      const data = d.data();
      return {
        userId: data['userId'] ?? d.id,
        role: data['role'],
        roleId: data['roleId'],
        permissions: data['permissions'],
        joinedAt: data['joinedAt'],
      };
    });
  }

  /** Resolve email/display-name per member via the shared backend. */
  async listMembersDetails(orgId: string): Promise<MemberDetails> {
    const callable = httpsCallable<{ organizationId: string }, { users: MemberDetails }>(
      this.firebase.functions,
      'listMembersDetailsOfOrganization',
    );
    const result = await callable({ organizationId: orgId });
    return result.data.users;
  }

  async updateMemberRole(orgId: string, memberId: string, roleId: string): Promise<void> {
    const memberRef = doc(this.firebase.firestore, 'organizations', orgId, 'members', memberId);
    await setDoc(memberRef, { role: roleId, roleId }, { merge: true });
  }

  async removeMember(orgId: string, memberId: string): Promise<boolean> {
    if (memberId === this.firebase.auth.currentUser?.uid) {
      throw new Error('Cannot remove yourself from the organization');
    }
    const callable = httpsCallable<{ organizationId: string; userId: string }, { success: boolean }>(
      this.firebase.functions,
      'removeUserFromOrganization',
    );
    const result = await callable({ organizationId: orgId, userId: memberId });
    return result.data.success;
  }

  async getRole(orgId: string, roleId: string): Promise<Role | null> {
    const snap = await getDoc(doc(this.firebase.firestore, 'organizations', orgId, 'roles', roleId));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      id: snap.id,
      name: data['name'],
      description: data['description'] || '',
      permissions: data['permissions'] || [],
      isSystem: data['isSystem'] ?? true,
      createdAt: data['createdAt'],
      updatedAt: data['updatedAt'],
      createdBy: data['createdBy'] || '',
    };
  }
}
